<template>
  <Modal
    v-model="show"
    :title="$t('bao-jing-dan-chuang')"
    :width="80"
    footer-hide
    @on-cancel="cancel"
    class-name="vertical-center-modal alarm-modal-management"
  >
    <audio ref="alarmAudio" loop autoplay></audio>

    <div class="management-container">
      <!-- 背景：沉浸式地图 -->
      <div class="map-background">
        <div id="alarmMapId" class="map-canvas"></div>
        <!-- HUD 报警内容展示（高亮置顶，突出紧急程度） -->
        <div class="hud-alarm-content" v-if="alarmData && (alarmData.content_text_parsed || alarmData.alarm_type)">
          <div class="hud-alarm-icon">
            <Icon type="md-alert" color="#fff" size="16" />
          </div>
          <span class="hud-alarm-text">{{ alarmData.content_text_parsed || alarmData.alarm_type }}</span>
        </div>
        <!-- 无定位信息提示 -->
        <div class="no-location-tip" v-if="alarmData && !hasAlarmLocation(alarmData)">
          <span>{{ $t('wu-ding-wei-xin-xi') }}</span>
        </div>
      </div>

      <!-- 左侧：悬浮报警卡片列表 -->
      <div class="white-panel panel-left">
        <div class="panel-header">
          <div class="header-title">
            <div class="status-indicator"></div>
            <span>{{ $t('bao-jing-lie-biao') }}</span>
          </div>
          <div class="panel-badge">{{ (alarmList || []).length }}</div>
        </div>

        <div class="list-scroll">
          <div v-if="!alarmList || alarmList.length === 0" class="empty-state">
            <Icon type="md-notifications-off" size="40" color="#f0f0f0" />
            <p>{{ $t('wu-bao-jing-xin-xi') }}</p>
          </div>
          <div
            v-else
            v-for="(item, index) in alarmList"
            :key="index"
            class="management-card"
            :class="{'is-active': index === currentIndex}"
            @click="selAlarm(item, index)"
          >
            <div class="card-body">
              <div class="card-icon-box">
                <img :src="$fullImage('/image/screen/alarm-'+item.alarm_type_id+'.png')" class="type-icon" />
              </div>
              <div class="card-info">
                <div class="info-type">{{ item.alarm_type }}</div>
                <div class="info-time">{{ item.create_time ? item.create_time.split(' ')[1] : '' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：详情面板 -->
      <div class="white-panel panel-right">
        <div class="panel-section">
          <div class="section-title">
            <Icon type="md-contact" size="16" />
            <span>{{ $t('dang-an-xian-xi') }}</span>
          </div>
          <div class="section-content">
            <template v-if="alarmData && alarmData.files">
              <div class="profile-hero">
                <Avatar :src="alarmData.files.head_img" size="60" class="clean-avatar" />
                <div class="hero-text">
                  <div class="h-name">{{ alarmData.files.name }}</div>
                  <div class="h-code">
                    {{ alarmData.files.code }}
                    <span v-if="alarmData.files.sex || alarmData.files.age">
                      （{{ alarmData.files.sex == 1 ? $t('nan') : alarmData.files.sex == 2 ? $t('nv') : '' }}{{ (alarmData.files.sex && alarmData.files.age) ? ', ' : '' }}{{ alarmData.files.age ? alarmData.files.age + ' ' + $t('sui') : '' }}）
                    </span>
                  </div>
                  <template v-if="alarmData.files">
                    <div v-if="alarmData.files.belong_name" class="h-belong">
                      <Icon type="md-business" size="12" />
                      {{ alarmData.files.belong_name }}
                    </div>
                  </template>
                </div>
              </div>
            </template>
            <div v-else class="no-data">{{ $t('wei-bang-ding') }}</div>
          </div>
        </div>

        <div class="divider"></div>

        <!-- 健康体征数据看板 -->
        <div class="panel-section">
          <div class="section-title">
            <Icon type="md-heart" size="16" />
            <span>{{ $t('he-xin-ti-zheng') }}</span>
          </div>
          <div class="metrics-grid">
            <div
              v-for="item in alarmMetricItems"
              :key="item.type"
              class="metric-card"
              :class="item.isAbnormal ? 'is-abnormal' : 'is-normal'"
            >
              <div class="metric-title">
                <div class="metric-label">
                  <span class="metric-dot" :style="{ backgroundColor: item.color }"></span>
                  <span class="metric-name">{{ item.label }}</span>
                </div>
                <span v-if="item.isAbnormal" class="abnormal-tag">{{ $t('yi-chang') }}</span>
              </div>
              <div class="metric-value">{{ item.displayValue }}</div>
              <div class="metric-meta">
                <span>{{ item.displayTime }}</span>
                <span>{{ item.unit }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="panel-section">
          <div class="section-title">
            <Icon type="md-cube" size="16" />
            <span>{{ $t('she-bei-xin-xi') }}</span>
          </div>
          <div class="section-content">
            <div class="profile-hero" v-if="alarmData && alarmData.model">
              <Avatar :src="alarmData.model.logo" size="60" class="clean-avatar" />
              <div class="hero-text">
                <div class="h-name">{{ alarmData.nickName || $t('wu') }}</div>
                <div class="h-code">{{ alarmData.device_id }}</div>
              </div>
            </div>
            <div class="data-item">
              <span class="label">{{ $t('an-zhuang-di-zhi') }}：</span>
              <span class="value">{{ (alarmData && alarmData.install_address) || '—' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import mapAdapter from '@/utils/mapAdapter'
import md5 from 'js-md5'
import { getHealthUnit } from '@/utils/healthUnit'
import { ALARM_CORE_METRICS, getMetricDef } from '@/utils/healthMetricTheme'

export default {
  name: 'DevAlarmPop',
  props: {
    mqttClient: { required: true }
  },
  data () {
    return {
      show: false,
      alarmList: [],
      alarmData: { files: {}, model: {}, institution: {} },
      audioElement: null,
      mapInfo: { AMap: null, Map: null, marker: null, circular: null },
      currentIndex: 0,
      subscribed: false,
      currentTopic: null,
      messageHandler: null
    }
  },
  mounted () {
    this.audioElement = this.$refs.alarmAudio
  },
  computed: {
    alarmMetricItems () {
      const hm = this.alarmData && this.alarmData.health_metrics
      return ALARM_CORE_METRICS.map(binding => {
        const def = getMetricDef(binding.type)
        let displayValue = '—'
        let isAbnormal = false
        let displayTime = '—'
        if (hm) {
          isAbnormal = !!hm[binding.abnormalKey]
          displayTime = this.formatMetricTime(hm[binding.timeKey])
          if (binding.bp) {
            displayValue = hm.systolic ? `${hm.systolic}/${hm.diastolic}` : '—'
          } else {
            displayValue = hm[binding.valueKey] != null && hm[binding.valueKey] !== '' ? hm[binding.valueKey] : '—'
          }
        }
        return {
          type: binding.type,
          color: def.color,
          label: this.$t(def.labelKey),
          displayValue,
          displayTime,
          unit: getHealthUnit(binding.type, this),
          isAbnormal
        }
      })
    }
  },
  watch: {
    show (val) {
      if (val) {
        this.$nextTick(() => {
          if (!this.mapInfo.Map) {
            this.initMap()
          } else {
            this.updateMap()
          }
        })
      }
    }
  },
  methods: {
    subscribe () {
      if (!this.mqttClient || !this.mqttClient.connected) return
      if (this.subscribed && this.currentTopic) this.unsubscribe()
      const domain = this.CurrentDomain || window.location.hostname
      let topic = md5(domain) + `/Device/Alarm`
      if (this.$store.state.applyInfo && this.$store.state.applyInfo.apply_id) {
        topic += `/apply_${this.$store.state.applyInfo.apply_id}`
      } else if (this.$store.state.userInfo) {
        topic += `/apply_${this.$store.state.userInfo.applyId}`
      }
      if (this.$store.state.labelType == 0) {
        topic += `/institution_0`
      } else if (this.$store.state.insInfo) {
        topic += `/institution_${this.$store.state.insInfo.id}`
      }
      if (this.subscribed && this.currentTopic === topic) return
      if (this.messageHandler) this.mqttClient.removeListener('message', this.messageHandler)
      this.messageHandler = (tpc, message) => {
        if (tpc !== topic) return
        try {
          const { alarmData, alarmAudio } = JSON.parse(message.toString())
          //获取多语言报警内容文本
          alarmData.content_text_parsed = this.getCurretLangeText(alarmData.languageAlarmCtnArr);
          //获取多语言报警类型文本
          alarmData.alarm_type =  this.getCurretLangeText(alarmData.alramLanguageArr)
          console.log(alarmData)
          this.alarmList.unshift(alarmData)
          this.alarmData = alarmData
          this.currentIndex = 0
          if (this.show) {
            // 弹窗已打开，show 值不变不会触发 watch，需手动更新地图
            this.$nextTick(() => this.updateMap())
          } else {
            this.show = true
            // watch 会触发，由 watch 负责调用 initMap/updateMap
          }
          if (alarmAudio && this.audioElement) {
            this.audioElement.src = alarmAudio
            this.audioElement.play().catch(e => console.error('播放失败:', e))
          }
        } catch (e) {
          console.error('消息处理失败:', e)
        }
      }
      this.mqttClient.on('message', this.messageHandler)
      this.mqttClient.subscribe(topic, { qos: 1 }, (err) => {
        if (!err) { this.subscribed = true; this.currentTopic = topic }
      })
    },
    unsubscribe () {
      if (!this.mqttClient) return
      if (this.messageHandler) { this.mqttClient.removeListener('message', this.messageHandler); this.messageHandler = null }
      if (this.currentTopic) { this.mqttClient.unsubscribe(this.currentTopic); this.currentTopic = null }
      this.subscribed = false
    },
    cancel () {
      this.alarmList = []
      if (this.audioElement) { this.audioElement.pause(); this.audioElement.src = '' }
    },
    formatMetricTime (timeStr) {
      if (!timeStr) return '—'
      // 只显示 MM-DD HH:mm 格式，例如 04-10 15:30
      try {
        const d = new Date(timeStr.replace(/-/g, '/'))
        if (isNaN(d.getTime())) return timeStr
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        const hour = String(d.getHours()).padStart(2, '0')
        const min = String(d.getMinutes()).padStart(2, '0')
        return `${month}-${day} ${hour}:${min}`
      } catch (e) {
        return timeStr
      }
    },
    normalizeCoordinate (value) {
      if (value === null || value === undefined || value === '') return null
      const coordinate = Number(value)
      return Number.isFinite(coordinate) ? coordinate : null
    },
    hasAlarmLocation (alarmData = this.alarmData) {
      const lng = this.normalizeCoordinate(alarmData && alarmData.lng)
      const lat = this.normalizeCoordinate(alarmData && alarmData.lat)
      return lng !== null && lat !== null
    },
    async initMap () {
      if (this.mapInfo.Map) return
      try {
        await mapAdapter.init()
        this.mapInfo.AMap = mapAdapter

        // 延迟渲染以确保 DOM 元素存在
        this.$nextTick(() => {
          const mapContainer = document.getElementById('alarmMapId')
          if (mapContainer) {
            const mapInstance = mapAdapter.createMap('alarmMapId', {
              viewMode: '2D',
              zoom: 17,
              mapStyle: 'amap://styles/grey',
              center: { lng: 116.397428, lat: 39.90923 },
              disableDefaultUI: true // 如果有这个选项，可以加上
            })

            if (mapInstance) {
              this.mapInfo.Map = mapInstance

              // 确保在地图实例化之后调用 updateMap，如果当前已有 alarmData，更新并展示标记
              if (this.show && this.alarmData && Object.keys(this.alarmData).length > 0) {
                this.updateMap()
              }
            }
          }
        })
      } catch (e) {
        console.error('Failed to init alarm map:', e)
      }
    },
    updateMap () {
      if (!this.mapInfo || !this.mapInfo.Map) return
      // 清除之前的标记
      if (this.mapInfo.marker) {
        mapAdapter.removeOverlays(this.mapInfo.Map, this.mapInfo.marker)
        this.mapInfo.marker = null
      }
      if (this.mapInfo.circular) {
        mapAdapter.removeOverlays(this.mapInfo.Map, this.mapInfo.circular)
        this.mapInfo.circular = null
      }

      // 判断是否有定位信息
      const lng = this.normalizeCoordinate(this.alarmData && this.alarmData.lng)
      const lat = this.normalizeCoordinate(this.alarmData && this.alarmData.lat)
      if (lng !== null && lat !== null) {
        // 有定位信息，显示标记
        let markerArray = []
        const marker = this.createMarker(lng, lat)
        if (marker) markerArray.push(marker)

        if (this.alarmData.fence) {
          const circular = this.createCircular(this.alarmData.fence.longitude, this.alarmData.fence.latitude, this.alarmData.fence.radius)
          if (circular) markerArray.push(circular)
        }
        if (markerArray.length > 0) {
          mapAdapter.setFitView(this.mapInfo.Map, markerArray)
        }
      }
    },
    createMarker (lng, lat) {
      const markerLng = this.normalizeCoordinate(lng)
      const markerLat = this.normalizeCoordinate(lat)
      if (markerLng === null || markerLat === null) return null

      // 脉冲信标：渐变核心 + 叹号 + 描边波纹环（三连波）+ 径向光晕
      const content = `
        <div class="xl-alarm-marker-wrap">
          <style>
            .xl-alarm-marker-wrap {
              position: relative;
              width: 72px;
              height: 72px;
              overflow: visible;
              pointer-events: none;
            }
            .xl-alarm-ripple {
              position: absolute;
              left: 50%;
              top: 50%;
              width: 36px;
              height: 36px;
              margin-left: -18px;
              margin-top: -18px;
              box-sizing: border-box;
              border-radius: 50%;
              border: 2px solid rgba(255, 77, 79, 0.92);
              background: transparent;
              z-index: 1;
              transform-origin: center center;
              will-change: transform, opacity;
              animation: xl-alarm-ripple-wave 2.1s infinite cubic-bezier(0.22, 0.61, 0.36, 1);
            }
            .xl-alarm-ripple-1 { animation-delay: 0s; }
            .xl-alarm-ripple-2 { animation-delay: 0.7s; }
            .xl-alarm-ripple-3 { animation-delay: 1.4s; }
            @keyframes xl-alarm-ripple-wave {
              0% { transform: scale(0.65); opacity: 0; }
              10% { opacity: 0.95; }
              100% { transform: scale(3.35); opacity: 0; }
            }
            .xl-alarm-pulse {
              position: absolute;
              left: 50%;
              top: 50%;
              width: 40px;
              height: 40px;
              margin-left: -20px;
              margin-top: -20px;
              border-radius: 50%;
              background: radial-gradient(circle, rgba(255, 77, 79, 0.45) 0%, rgba(255, 77, 79, 0.12) 45%, transparent 72%);
              z-index: 2;
              transform-origin: center center;
              will-change: transform, opacity;
              animation: xl-alarm-pulse-out 2s ease-out infinite;
            }
            .xl-alarm-pulse-2 { animation-delay: 1s; }
            @keyframes xl-alarm-pulse-out {
              0% { transform: scale(0.75); opacity: 1; }
              100% { transform: scale(2.85); opacity: 0; }
            }
            .xl-alarm-marker-core {
              position: absolute;
              left: 50%;
              top: 50%;
              transform: translate(-50%, -50%);
              width: 32px;
              height: 32px;
              background: linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%);
              border-radius: 50%;
              border: 3px solid rgba(255, 255, 255, 0.9);
              box-shadow: 0 2px 12px rgba(255, 77, 79, 0.6), 0 0 20px rgba(255, 77, 79, 0.35);
              z-index: 5;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .xl-alarm-icon-inner::before {
              content: '!';
              font-size: 18px;
              font-weight: 900;
              color: #fff;
              line-height: 1;
              font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
            }
          </style>
          <div class="xl-alarm-ripple xl-alarm-ripple-1"></div>
          <div class="xl-alarm-ripple xl-alarm-ripple-2"></div>
          <div class="xl-alarm-ripple xl-alarm-ripple-3"></div>
          <div class="xl-alarm-pulse"></div>
          <div class="xl-alarm-pulse xl-alarm-pulse-2"></div>
          <div class="xl-alarm-marker-core">
            <span class="xl-alarm-icon-inner"></span>
          </div>
        </div>
      `
      const marker = mapAdapter.createMarker({
        position: { lng: markerLng, lat: markerLat },
        content: content,
        offset: { x: -36, y: -36 },
        zIndex: 120
      })
      if (marker) {
        this.mapInfo.marker = marker
        mapAdapter.addOverlays(this.mapInfo.Map, marker)
      }
      return marker
    },
    createCircular (lng, lat, radius) {
      const centerLng = this.normalizeCoordinate(lng)
      const centerLat = this.normalizeCoordinate(lat)
      const circleRadius = Number(radius)
      if (centerLng === null || centerLat === null || !Number.isFinite(circleRadius) || circleRadius <= 0) return null

      const circular = mapAdapter.createCircle({
        center: { lng: centerLng, lat: centerLat },
        radius: circleRadius,
        strokeColor: '#FF4D4F',
        strokeOpacity: 0.5,
        strokeWeight: 2,
        fillColor: '#FF4D4F',
        fillOpacity: 0.05
      })
      if (circular) {
        this.mapInfo.circular = circular
        mapAdapter.addOverlays(this.mapInfo.Map, circular)
      }
      return circular
    },
    selAlarm (data, index) {
      this.currentIndex = index
      this.alarmData = data
      this.updateMap()
    },
    calculateHeight () {},
    handleResize () {},
    //根据当前语言获取多语言json字符串中对应的文本
    getCurretLangeText(langObj) {

      const language = localStorage.getItem('language') || 'zh-cn'

      let translatedCtn = null

      if (typeof langObj === 'string') {
        try { langObj = JSON.parse(langObj) } catch (e) {}
      }
      if (langObj && typeof langObj === 'object') {
        const shortLang = language.split('-')[0]
        translatedCtn = langObj[language] || langObj[shortLang]
      }

      return translatedCtn;
      }
    },
  beforeDestroy () { this.cancel(); this.unsubscribe() },
}
</script>

<style scoped>
.management-container {
  position: relative;
  height: 720px;
  background: #f0f2f5;
  overflow: hidden;
}

/* 背景地图 - 移除圆角 */
.map-background {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.map-canvas {
  width: 100%;
  height: 100%;
}

/* HUD 报警内容 - 高亮置顶 */
.hud-alarm-content {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(237, 64, 20, 0.92);
  padding: 8px 20px 8px 10px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10;
  box-shadow: 0 4px 18px rgba(237, 64, 20, 0.4);
  max-width: 60%;
}

.hud-alarm-icon {
  width: 28px;
  height: 28px;
  background: rgba(255,255,255,0.25);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.hud-alarm-text {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  white-space: normal;
  word-break: break-word;
  line-height: 1.5;
}

/* 悬浮纯白面板 */
.white-panel {
  position: absolute;
  top: 24px;
  bottom: 24px;
  background: #ffffff;
  border-radius: 16px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  border: 1px solid #f0f0f0;
}

.panel-left {
  left: 24px;
  width: 340px;
}

.panel-right {
  right: 24px;
  width: 380px;
  padding: 24px;
  overflow-y: auto;
  scrollbar-width: none;
}

.panel-right::-webkit-scrollbar {
  display: none;
}

/* 报警列表 */
.panel-header {
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  color: #1a1b1d;
}

.status-indicator {
  width: 8px;
  height: 8px;
  background: #ff4d4f;
  border-radius: 50%;
  box-shadow: 0 0 0 4px rgba(255, 77, 79, 0.1);
}

.panel-badge {
  background: #f5f7fa;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  color: #909399;
}

.list-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 5px 16px 24px;
}

.management-card {
  padding: 18px;
  border-radius: 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fff;
  border: 1px solid transparent;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.management-card:hover {
  background: #f8f9fb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.management-card.is-active {
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%);
  border-color: #91d5ff;
  box-shadow: 0 4px 16px rgba(24, 144, 255, 0.15);
}

.card-body {
  display: flex;
  gap: 18px;
  align-items: center;
}

.card-icon-box {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf0 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.type-icon {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.card-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  min-width: 0;
}

.info-type {
  font-size: 16px;
  font-weight: 600;
  color: #1a1b1d;
  line-height: 1.4;
  word-break: break-word;
}

.info-time {
  font-size: 13px;
  color: #909399;
  font-weight: 500;
}

/* 详情面板 */
.panel-section {
  margin-bottom: 24px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #bfbfbf;
  text-transform: uppercase;
  margin-bottom: 20px;
}

.profile-hero {
  display: flex;
  align-items: center;
  gap: 16px;
}

.clean-avatar {
  border: 2px solid #f0f2f5;
  background: #f5f7fa;
}

.h-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1b1d;
}

.h-code {
  font-size: 13px;
  color: #909399;
}

.h-belong {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 12px;
  color: #fff;
  background: linear-gradient(135deg, #1890ff, #096dd9);
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.data-item {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
  background: #f8f9fb;
  border-radius: 12px;
}

.label {
  font-size: 13px;
  color: #909399;
}

.value {
  font-size: 14px;
  font-weight: 600;
  color: #1a1b1d;
}

.divider {
  height: 1px;
  background: #f0f0f0;
  margin-bottom: 24px;
}

.list-scroll {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}

.list-scroll::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Opera */
  width: 0;
}

.no-location-tip {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  padding: 12px;
  background: rgba(255, 255, 255, 0.95);
  color: #666;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

::v-deep .alarm-modal-management .ivu-modal-body {
  padding: 0;
  background: #f0f2f5;
}

.no-data {
  text-align: center;
  color: #666;
}

/* 健康体征 2x2 看板 */
.metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.metric-card {
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s;
}

.metric-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.metric-label {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.metric-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.metric-name {
  font-size: 13px;
  color: #515a6e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.metric-value {
  font-size: 20px;
  font-weight: 600;
  margin: 8px 0 4px;
  color: #17233d;
  line-height: 1.2;
}

.metric-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #808695;
}

.is-normal {
  background: #fff;
  border: 1px solid #e8eaec;
}

.is-abnormal {
  background: #fff1f0;
  border: 1px solid #ffa39e;
}

.is-abnormal .metric-value {
  color: #ed4014;
}

.abnormal-tag {
  flex-shrink: 0;
  font-size: 11px;
  color: #ed4014;
  background: #fff1f0;
  padding: 0 4px;
  border-radius: 3px;
  border: 1px solid #ffa39e;
}
</style>



// WEBPACK FOOTER //
// src/components/msgNotice/DevAlarmPop.vue